import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { SIMULATE_PRESET_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/simulate-preset';
import { SimulateCustomPreset } from 'dashboard/models/simulate/preset';
import Button from 'design-system/components/Button';
import { Input } from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { PresetType } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface SaveAsPresetModalProps {
  existingPresets: SimulateCustomPreset[];
  presetType: PresetType;
  onSave: ({ name, description }: { name: string; description?: string }) => void;
  hideModal: () => void;
}

export const SaveAsPresetModal = ({
  existingPresets,
  presetType,
  onSave,
  hideModal,
}: SaveAsPresetModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<{
    name: string;
    description?: string;
  }>({
    mode: 'onChange',
    defaultValues: {},
  });

  const existingPresetNamesForType = useMemo(() => {
    return existingPresets.reduce((presetNames: string[], preset) => {
      if (preset.type === presetType) {
        return [...presetNames, preset.attributes.name];
      }
      return presetNames;
    }, []);
  }, [existingPresets, presetType]);

  return (
    <Form onSubmit={handleSubmit(onSave)}>
      <ModalContainer>
        <ModalHeader onClose={hideModal}>Create new preset</ModalHeader>
        <ModalBody>
          <FormGroup>
            <FormControlLabel
              id='save-as-preset__text-preset-type'
              htmlFor='save-as-preset__text-preset-type'
            >
              Preset Type
            </FormControlLabel>
            <Paragraph>{SIMULATE_PRESET_TYPE_NAMES_SHORT[presetType] || presetType}</Paragraph>
            <Spacer height={2} />
            <FormControlLabel id='save-as-preset__input-name' htmlFor='save-as-preset__input-name'>
              Name of preset
            </FormControlLabel>
            <Controller
              name='name'
              control={control}
              rules={{
                required: 'Name is required',
                validate: {
                  notEmpty: (value) => {
                    if (!value.trim()) {
                      return 'Name is required';
                    }
                  },
                  notDuplicateName: (value) => {
                    if (existingPresetNamesForType.includes(value)) {
                      return 'Name is already taken for preset of this type';
                    }
                  },
                },
              }}
              render={({ field: { value, onChange } }) => (
                <Input
                  id='save-as-preset__input-name'
                  aria-labelledby='save-as-preset__input-name'
                  placeholder='What does this preset represent?'
                  disabled={isSubmitting}
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                />
              )}
            />
            {errors['name'] && <FormError>{errors['name'].message}</FormError>}
            <Spacer height={2} />
            <FormControlLabel
              id='save-as-preset__input-description'
              htmlFor='save-as-preset__input-description'
            >
              Description (optional)
            </FormControlLabel>
            <Controller
              name='description'
              control={control}
              rules={{
                maxLength: { value: 200, message: 'Please limit description to 200 characters' },
              }}
              render={({ field: { value, onChange } }) => (
                <TextArea
                  id='save-as-preset__input-description'
                  aria-labelledby='save-as-preset__input-description'
                  placeholder='What are the details of this preset?'
                  disabled={isSubmitting}
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                />
              )}
            />
            {errors['description'] && <FormError>{errors['description'].message}</FormError>}
          </FormGroup>
          <HStack equal={true} spacing={Spacing[4]}>
            <Button
              variant='basic'
              id='tabbed-submit-modal__cancel'
              disabled={isSubmitting}
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='tabbed-submit-modal__submit'
              disabled={isSubmitting || !isValid}
              isPending={isSubmitting}
            >
              Create Preset
            </Button>
          </HStack>
        </ModalBody>
      </ModalContainer>
    </Form>
  );
};
